import { useAuth } from '@/context/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Monitor, ExternalLink, Mail, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { WEBMAIL_URL } from '@/lib/constants'

// CHECKPOINT: PRD-10 FR-10.14.1 — Webmail available at webmail.purpletoadmail.com.
// CHECKPOINT: PRD-10 FR-10.14.2 — Positioning: "Basic web access for emergencies. Your real email app is better."
export function Webmail() {
  const { user } = useAuth()

  // CHECKPOINT: PRD-10 FR-10.14.6 — Single-click "Open Webmail" button in dashboard sidebar.
  const webmailUrl = WEBMAIL_URL

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Webmail</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Emergency web access to your email
        </p>
      </div>

      {/* CHECKPOINT: PRD-10 FR-10.14.3 — Persistent banner at top. */}
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-4 flex items-start gap-3">
        <Monitor className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-200">
            For daily use, connect Thunderbird, Apple Mail, or Gmail app
          </p>
          <p className="text-xs text-amber-300/70 mt-1">
            Webmail is basic emergency access only. A real email client gives you a much better experience.
          </p>
          <div className="mt-3">
            <Button variant="outline" size="sm" asChild>
              <Link to="/mailboxes">
                <Mail className="mr-2 h-4 w-4" />
                Set up a mail app
                <ArrowRight className="ml-1.5 h-3 w-3" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* CHECKPOINT: PRD-10 FR-10.14.4 — Skinning reference and launch card. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Monitor className="h-4 w-4 text-muted-foreground" />
            Open webmail
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Sign in with your mailbox email address and password. No separate account needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild>
              <a
                href={webmailUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Webmail
              </a>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/mailboxes">
                Manage mailboxes
              </Link>
            </Button>
          </div>
          {user?.email && (
            <p className="text-xs text-muted-foreground">
              Signed into PurpleToad Mail as {user.email}. Use your mailbox credentials in webmail.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
